import React, { useMemo, useRef, useState } from "react";
import { AdminService } from "../../services/adminService";
import "./AdminToolbar.css";
import initialVideos from "../../data/initial_videos.json";

// 小型の制御アイコン
const PlayIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5,3 19,12 5,21" />
  </svg>
);

const PauseIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <rect x="6" y="4" width="4" height="16" />
    <rect x="14" y="4" width="4" height="16" />
  </svg>
);

const NextIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <polygon points="5,4 15,12 5,20" />
    <rect x="17" y="4" width="2" height="16" />
  </svg>
);

interface AdminToolbarProps {
  onLogout?: () => void;
}

const AdminToolbar: React.FC<AdminToolbarProps> = ({ onLogout }) => {
  const [videoId, setVideoId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(0);
  const [seekSec, setSeekSec] = useState("");
  const [autoRotate, setAutoRotate] = useState(true);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(() => window.innerWidth <= 600);

  const searchContainerRef = useRef<HTMLDivElement | null>(null);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const filteredOptions = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const list: any[] = Array.isArray(initialVideos) ? (initialVideos as any[]) : [];
    if (!q) {
      return list.slice(0, 30);
    }
    return list
      .filter((v) => {
        const title = String(v.title || "").toLowerCase();
        const vid = String(v.videoId || "").toLowerCase();
        return title.includes(q) || vid.includes(q);
      })
      .slice(0, 50);
  }, [searchQuery]);

  const showMessage = (text: string, duration = 2300) => {
    setMessage(text);
    setTimeout(() => setMessage(""), duration);
  };

  const withLoading = async (callback: () => Promise<void> | void) => {
    if (isLoading) {
      return;
    }
    setIsLoading(true);
    try {
      await callback();
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggle = () => {
    setIsCollapsed((prev) => !prev);
  };

  const panelId = "admin-toolbar-panel";

  return (
    <div className="admin-toolbar-container">
      <button
        type="button"
        className="admin-toolbar-toggle-button"
        onClick={handleToggle}
        aria-expanded={!isCollapsed}
        aria-controls={panelId}
      >
        <span className="admin-toolbar-toggle-icon" aria-hidden="true">
          {isCollapsed ? "▸" : "▾"}
        </span>
        <span className="admin-toolbar-toggle-label">ADMIN CONTROL</span>
        <span className="admin-toolbar-toggle-status">
          {isCollapsed ? "タップで開く" : "タップで閉じる"}
        </span>
      </button>
      <div className="admin-toolbar-panel">
        <div className="admin-toolbar-header">
          <span className="admin-toolbar-title">CONTROL PANEL</span>
          <span className="admin-toolbar-badge">ADMIN MODE</span>
        </div>
        <div
          className={`admin-toolbar-group${isCollapsed ? " is-collapsed" : ""}`}
          aria-hidden={isCollapsed}
          id={panelId}
        >
          <div className="admin-toolbar-field">
            <label className="admin-toolbar-label" htmlFor="admin-video-id">
              動画を検索（タイトル / 動画ID）
            </label>
            <div className="admin-select-container" ref={searchContainerRef}>
              <input
                id="admin-video-id"
                ref={searchInputRef}
                className="admin-toolbar-input"
                value={searchQuery}
                onChange={(event) => {
                  const value = event.target.value;
                  setSearchQuery(value);
                  setVideoId(value.trim());
                }}
                onFocus={() => setIsDropdownOpen(true)}
                onBlur={() => setTimeout(() => setIsDropdownOpen(false), 120)}
                onKeyDown={(e) => {
                  if (!isDropdownOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
                    setIsDropdownOpen(true);
                  }
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightIndex((prev) => Math.min(prev + 1, Math.max(filteredOptions.length - 1, 0)));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightIndex((prev) => Math.max(prev - 1, 0));
                  } else if (e.key === "Enter") {
                    if (isDropdownOpen && filteredOptions[highlightIndex]) {
                      const v: any = filteredOptions[highlightIndex];
                      setSearchQuery(String(v.title || ""));
                      setVideoId(String(v.videoId || ""));
                      setIsDropdownOpen(false);
                    }
                  } else if (e.key === "Escape") {
                    setIsDropdownOpen(false);
                  }
                }}
                placeholder="タイトルや動画IDで検索 / 直接ID入力も可"
                disabled={isLoading}
              />
              {isDropdownOpen && filteredOptions.length > 0 && (
                <div className="admin-select-dropdown" role="listbox">
                  {filteredOptions.map((v: any, idx: number) => (
                    <div
                      key={String(v.videoId)}
                      role="option"
                      aria-selected={idx === highlightIndex}
                      className={`admin-select-option${idx === highlightIndex ? " is-highlighted" : ""}`}
                      onMouseDown={() => {
                        setSearchQuery(String(v.title || ""));
                        setVideoId(String(v.videoId || ""));
                        setIsDropdownOpen(false);
                        // 入力欄へフォーカスを戻す
                        setTimeout(() => searchInputRef.current?.focus(), 0);
                      }}
                    >
                      <div className="admin-select-option-title">{String(v.title || "")}</div>
                      <div className="admin-select-option-meta">{String(v.videoId || "")}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              type="button"
              className="admin-toolbar-button is-primary"
              onClick={() =>
                withLoading(async () => {
                  const trimmed = videoId.trim();
                  if (!trimmed) {
                    showMessage("動画IDを入力してください", 2000);
                    return;
                  }
                  const ok = await AdminService.selectVideo(trimmed);
                  showMessage(ok ? "動画を読み込みました" : "動画の選択に失敗しました", 2500);
                })
              }
              disabled={isLoading}
            >
              リストから読み込む
            </button>
          </div>

          <div className="admin-toolbar-actions admin-toolbar-actions--controls">
            <button
              type="button"
              className="admin-toolbar-button admin-toolbar-button--icon"
              onClick={() =>
                withLoading(async () => {
                  await AdminService.play();
                  showMessage("再生を開始しました");
                })
              }
              disabled={isLoading}
              title="再生"
              aria-label="再生"
            >
              <PlayIcon size={18} />
            </button>
            <button
              type="button"
              className="admin-toolbar-button admin-toolbar-button--icon"
              onClick={() =>
                withLoading(async () => {
                  await AdminService.pause();
                  showMessage("一時停止しました");
                })
              }
              disabled={isLoading}
              title="一時停止"
              aria-label="一時停止"
            >
              <PauseIcon size={18} />
            </button>
            <button
              type="button"
              className="admin-toolbar-button admin-toolbar-button--icon"
              onClick={() =>
                withLoading(async () => {
                  await AdminService.next();
                  showMessage("次の動画に切り替えました");
                })
              }
              disabled={isLoading}
              title="次の動画"
              aria-label="次の動画"
            >
              <NextIcon size={18} />
            </button>
          </div>

          <div className="admin-toolbar-field">
            <label className="admin-toolbar-label" htmlFor="admin-seek-sec">
              指定時間までシーク
            </label>
            <div className="admin-toolbar-actions is-input-group">
              <input
                id="admin-seek-sec"
                className="admin-toolbar-input"
                value={seekSec}
                onChange={(event) => setSeekSec(event.target.value)}
                placeholder="時間を入力 (例: 1:30 または 01:30:00)"
                disabled={isLoading}
              />
              <button
                type="button"
                className="admin-toolbar-button"
                onClick={() =>
                  withLoading(async () => {
                    const trimmed = seekSec.trim();
                    if (!trimmed) {
                      showMessage("時間を入力してください", 2500);
                      return;
                    }

                    // 時分秒形式を秒数に変換
                    const parseTimeToSeconds = (timeStr: string): number | null => {
                      const parts = timeStr.split(":").map(p => parseInt(p.trim(), 10));

                      if (parts.length === 3) {
                        // HH:MM:SS形式
                        const [hours, minutes, seconds] = parts;
                        if (parts.some(isNaN)) return null;
                        return hours * 3600 + minutes * 60 + seconds;
                      } else if (parts.length === 2) {
                        // MM:SS形式
                        const [minutes, seconds] = parts;
                        if (parts.some(isNaN)) return null;
                        return minutes * 60 + seconds;
                      } else if (parts.length === 1) {
                        // 秒数のみ（後方互換性のため）
                        const seconds = parts[0];
                        if (isNaN(seconds)) return null;
                        return seconds;
                      }

                      return null;
                    };

                    const sec = parseTimeToSeconds(trimmed);
                    if (sec === null || sec < 0) {
                      showMessage("正しい時間形式で入力してください (例: 1:30 または 01:30:00)", 3000);
                      return;
                    }

                    await AdminService.seek(sec);

                    // 秒数を時分秒形式に戻して表示
                    const hours = Math.floor(sec / 3600);
                    const minutes = Math.floor((sec % 3600) / 60);
                    const seconds = sec % 60;
                    const timeStr = hours > 0
                      ? `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`
                      : `${minutes}:${String(seconds).padStart(2, "0")}`;

                    showMessage(`${timeStr} 地点に移動しました`);
                  })
                }
                disabled={isLoading}
              >
                シーク
              </button>
            </div>
          </div>

          <div className="admin-toolbar-toggle">
            <input
              id="admin-auto-rotate"
              type="checkbox"
              checked={autoRotate}
              onChange={(event) =>
                withLoading(async () => {
                  const checked = event.target.checked;
                  setAutoRotate(checked);
                  await AdminService.toggleAutoRotate(checked);
                  showMessage(checked ? "自動ローテーションを有効化" : "自動ローテーションを停止", 2500);
                })
              }
              disabled={isLoading}
            />
            <label htmlFor="admin-auto-rotate">
              自動ローテーション
              <span>無効化すると現在の動画が終了しても自動で切り替わりません</span>
            </label>
          </div>

          <div className="admin-toolbar-actions">
            <button
              type="button"
              className="admin-toolbar-button is-danger"
              onClick={() =>
                withLoading(async () => {
                  await AdminService.logout();
                  onLogout?.();
                  showMessage("ログアウトしました", 2600);
                })
              }
              disabled={isLoading}
            >
              ログアウト
            </button>
          </div>

          {message && <div className="admin-toolbar-message">{message}</div>}
        </div>
      </div>
    </div>
  );
};

export default AdminToolbar;


